import { isEmpty, pickBy, get } from 'lodash'
import { parseRef, scrubUndefined } from "../../firebase"
import { defaultListener, generateId } from "../helper"
import drawers from "./drawers"

export default {
  state: {
    $events: undefined,
    events: {},
  },
  getters: {
    getEvent: (state) => (id) => {
      return state.events[ id ] || {}
    },
    allEvents: (state) => returnMap => {
      return returnMap
        ? state.events
        : Object.values(state.events)
    },
    archivedEvents(state, getters) {
      return getters.allEvents(false)
        .filter(e => e.isArchived)
    },
    eventTree(state, getters) {
      const eventsMap = getters.allEvents(true)

      const eventGroupsMap = getters.allEventGroups(true)
      const eventItemsMap = getters.allEventItems(true)

      const childEventMap = Object.values(eventGroupsMap).reduce((prev, curr) => {
        prev[ curr.completeEvent ] = curr.completeEvent
        return prev
      }, {})
      const rootEventsMap = Object.values(eventsMap).reduce((prev, curr) => {
        if (!(curr.id in childEventMap)) {
          prev[ curr.id ] = curr
        }
        return prev
      }, {})

      const buildChildren = (eventId) => {
        // get event items
        const eventItems = Object.values(eventItemsMap).filter(e => e.eventId === eventId)

        // get event Groups
        const eventGroups = Object.values(eventGroupsMap).filter(e => e.eventId === eventId)

        /**
         * Each object type's (event, eventGroup, eventItem) attributes are included in the tree item.
         * This means that each tree item may have different parameters, expect for the following:
         *    children: Array
         *    name: string
         *    id: string
         *    type: string
         *
         * These are required in every tree item
         */

        const formatItemForTree = (item) => {
          return {
            ...item, // item attributes may be used in the view
            id: item.id,
            name: item.label,
            type: item.type,
            children: []
          }
        }

        const items = eventItems
          .filter(ei => !ei.eventGroupId)
          .map(ei => formatItemForTree(ei))

        const groups = eventGroups.map(eventGroup => {
          const itemsInGroup = Object.values(eventItemsMap)
            .filter(ei => ei.eventGroupId === eventGroup.id)
            .map(ei => formatItemForTree(ei))

          let completeEvent = null
          if (eventGroup.completeEvent) {
            const event = eventsMap[ eventGroup.completeEvent ]
            completeEvent = {
              ...event, // event attributes may be used in the view
              id: event.id,
              name: event.label,
              type: 'complete_event',
              children: [ ...buildChildren(event.id) ]
            }
          }

          return {
            ...eventGroup,
            id: eventGroup.id,
            name: `${eventGroup.label}`,
            type: 'group',
            children: isEmpty(completeEvent)
              ? [ ...itemsInGroup ]
              : [ ...itemsInGroup, completeEvent ]
          }
        })

        return [ ...items, ...groups ]
      }

      const finalMap = {}
      for (const eventId in rootEventsMap) {
        const event = rootEventsMap[ eventId ]

        const eventForTree = {
          ...event,
          id: event.id,
          name: event.label,
          type: 'event',
          children: buildChildren(eventId)
        }

        finalMap[ eventId ] = eventForTree
      }

      return Object.values(finalMap)
        .filter(e => !e.isArchived)
        .sort((a, b) => {
          return !!b.isActive - !!a.isActive
        })
    },
    allActiveNonSystemEvents(state) {
      return Object.values(
        pickBy(state.events, e => !e.isSystemEvent && e.isActive)
      )
    },
    activeEvents(state) {
      return Object.values(
        pickBy(state.events, e => e.isActive)
      )
    },
    inactiveEvents(state) {
      return Object.values(
        pickBy(state.events, e => !e.isActive)
      )
    }
  },
  actions: {
    updateEvent({ getters }, data) {
      const { isActive, isArchived, label, description, id } = data
      const payload = { isActive, isArchived, label, description }
      const sessionTenant = getters.getSessionTenant
      const merge = true

      scrubUndefined(payload)

      this.$firebase.firestore()
        .collection('tenants')
        .doc(sessionTenant)
        .collection('events')
        .doc(id)
        .set(payload, { merge })
        .catch(e => console.log(e))
    },
    createEvent({ getters }, data) {
      const {
        isActive = true,
        label = '',
        description = '',
        isArchived = false
      } = data

      const payload = { isActive, isArchived, label, description }
      const sessionTenant = getters.getSessionTenant
      const merge = true

      const eventId = generateId(sessionTenant, 'events')

      this.$firebase.firestore()
        .collection('tenants')
        .doc(sessionTenant)
        .collection('events')
        .doc(eventId)
        .set(payload, { merge })
        .catch(e => console.log(e))
    },
    ensureEvent({ state, getters, commit }, eventId) {
      const event = getters.getEvent(state, eventId)
      const eventIsMissing = isEmpty(event)
      const sessionTenant = getters.getSessionTenant

      if (eventIsMissing) {
        this.$firebase.firestore()
          .collection('tenants')
          .doc(sessionTenant)
          .collection('events')
          .doc(eventId)
          .get()
          .then(snap => {
            commit('ADD_EVENT', parseRef(snap))
          })
      }
    },
    initEvents({ commit, getters, dispatch }) {
      const sessionTenant = getters.getSessionTenant

      this.$firebase.firestore()
        .collection('tenants')
        .doc(sessionTenant)
        .collection('events')
        .onSnapshot(snap => {
            snap.docChanges().forEach(change => {
              if (change.type === 'added') {
                const source = change.doc.metadata.hasPendingWrites ? 'Local' : 'Server'

                // Haven't fully decided how to handle this.
                // Since all entities are stored in a map, it doesnt matter if we receive
                // multiple update changes for the same document
                if (source === 'Server' || true) {
                  const { id } = change.doc
                  dispatch('initEventItems', id)
                  dispatch('initEventGroups', id)
                  commit('ADD_EVENT', { id, ...change.doc.data() })
                }
              }

              if (change.type === 'removed') {
                commit('REMOVE_EVENT', change.doc.id)
              }

              if (change.type === 'modified') {
                const { id } = change.doc
                commit('ADD_EVENT', { id, ...change.doc.data() })
              }
            })
          },
          e => {
            console.log(e)
          }
        )
    },
    async deleteCompleteEvent({ getters, dispatch }, data) {
      const { id } = data
      const sessionTenant = getters.getSessionTenant

      const eventGroups = await this.$db.collection('tenants')
        .doc(sessionTenant)
        .collection('eventGroups')
        .where('completeEvent', '==', id)
        .get()
        .then(parseRef)

      const eventGroup = get(eventGroups, '[0]')

      // If there wasn't an event group with this eventId, its a root event
      if (eventGroup) {
        const deleteEventDocument = async () => {
          await this.$firebase.firestore()
            .collection('tenants')
            .doc(sessionTenant)
            .collection('events')
            .doc(id)
            .delete()
            .catch(e => console.log(e))
        }

        const eventGroupPayload = {
          id: eventGroup.id,
          completeEvent: null
        }

        await dispatch('updateEventGroup', eventGroupPayload)
          .then(deleteEventDocument)
      }
    },
    async runEvent({ }, data) {
      const { id } = data

      await this.$api.post(`/api/event/${id}/run`)
        .catch(e => { console.log(e) })
    }
  },
  mutations: {
    ADD_EVENT(state, event) {
      const { id } = event

      if (id) {
        const newState = { ...state.events }
        newState[ id ] = event
        state.events = newState
      }
    },
    REMOVE_EVENT(state, id) {
      if (state.events[ id ]) {
        const newState = { ...state.events }
        delete newState[ id ]
        state.events = newState
      }
    }
  },
}
